import type { FC } from 'react';

import { useIntl } from 'react-intl';

import { Button, Modal, Spin } from 'antd';

import type { ReplacementJourneyImpact } from '@src/packages/workJourney/utils/evaluateReplacementJourneyImpact';
import IntlMessages from '@src/util/IntlMessages';

import styles from './styles.module.less';

export type WorkJourneyCoverReplaceConfirmModalProps = {
  open: boolean;
  loading?: boolean;
  impact: ReplacementJourneyImpact | null;
  onConfirm: () => void;
  onChooseAnother: () => void;
};

export const WorkJourneyCoverReplaceConfirmModal: FC<WorkJourneyCoverReplaceConfirmModalProps> = ({
  open,
  loading = false,
  impact,
  onConfirm,
  onChooseAnother,
}) => {
  const intl = useIntl();

  const hasImpact = impact?.kind === 'impact';
  const userName = impact?.userDisplayName ?? '—';
  const journeyName =
    impact?.kind === 'impact'
      ? impact.currentJourneyName
      : impact?.currentJourneyName ||
        intl.formatMessage({ id: 'workJourney.management.cover.replace.noJourney' });

  return (
    <Modal
      open={open}
      onCancel={onChooseAnother}
      closable={false}
      destroyOnClose
      width={480}
      className={styles.modal}
      title={intl.formatMessage({
        id: 'workJourney.management.cover.replace.confirm.title',
      })}
      footer={null}
    >
      <Spin spinning={loading}>
        <div className={styles.userCard}>
          <div className={styles.userRow}>
            <span className={styles.label}>
              <IntlMessages id="workJourney.management.cover.replace.confirm.user" />
            </span>
            <span className={styles.value}>{userName}</span>
          </div>
          <div className={styles.userRow}>
            <span className={styles.label}>
              <IntlMessages id="workJourney.management.cover.replace.confirm.currentJourney" />
            </span>
            <span className={styles.value}>{journeyName}</span>
          </div>
        </div>

        <p className={styles.message}>
          {loading || !impact ? (
            <IntlMessages id="general.loading" />
          ) : impact.kind === 'impact' ? (
            intl.formatMessage(
              { id: 'workJourney.management.cover.replace.confirm.impactMessage' },
              {
                journeyName: impact.currentJourneyName,
                remaining: impact.remainingMembers,
                required: impact.requiredMembers,
              },
            )
          ) : (
            <IntlMessages id="workJourney.management.cover.replace.confirm.noImpactMessage" />
          )}
        </p>

        <div className={styles.actions}>
          <Button type="primary" onClick={onConfirm} disabled={loading || !impact} block>
            {intl.formatMessage({
              id: hasImpact
                ? 'workJourney.management.cover.replace.confirm.selectAnyway'
                : 'workJourney.management.cover.replace.confirm.select',
            })}
          </Button>
          <Button onClick={onChooseAnother} disabled={loading} block>
            {intl.formatMessage({
              id: 'workJourney.management.cover.replace.confirm.chooseAnother',
            })}
          </Button>
        </div>

        <p className={styles.footnote}>
          <IntlMessages id="workJourney.management.cover.replace.confirm.footnote" />
        </p>
      </Spin>
    </Modal>
  );
};
